import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { BellIcon, PlusIcon, PriceTagIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { Fragment } from 'react/jsx-runtime'
import { toast } from 'sonner'

import { API } from '../../../api/apis.config'
import { getTransfer } from '../../../api/transfers/get-transfer'
import { createTransferPayment } from '../../../api/trip-bookings/create-transfer-payment'
import { getTransferPayments } from '../../../api/trip-bookings/get-transfer-payments'
import { updateTransferPayment } from '../../../api/trip-bookings/update-transfer-payments'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import { type TransferPayment } from '../../../lib/types/models/transfer/transfer-payments'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../elements/dynamic-table'
import NestedDataTable from '../../elements/nested-table'
import TransferPaymentForm from './transfer-payment-form'

interface TransferPaymentsProps {
  transferId: number
}

const TransferPayments = ({ transferId }: TransferPaymentsProps) => {
  const tableRef = useRef<DynamicTableRef<TransferPayment>>(null)
  const queryClient = useQueryClient()

  const invalidatePayments = () =>
    queryClient.invalidateQueries({ exact: true, queryKey: ['transferPayments', transferId] })

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    TransferPayment
  >(['create', 'edit'])

  const { data: transfer, isLoading } = useQuery({
    queryKey: ['transfer', transferId],
    queryFn: () => getTransfer(transferId)
  })

  const { data: transferPayments, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['transferPayments', transferId],
    queryFn: () => getTransferPayments(transferId)
  })

  const { mutateAsync: createPayment } = useMutation({
    mutationFn: createTransferPayment,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      invalidatePayments()
      closeModel('create')
      toast.success('Paiement créé avec succès')
    }
  })

  const { mutateAsync: updatePayment } = useMutation({
    mutationFn: updateTransferPayment,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      invalidatePayments()
      closeModel('edit')
      toast.success('Paiement modifié avec succès')
    }
  })

  const columns: Column<TransferPayment>[] = [
    {
      accessorKey: 'amount',
      header: 'Montant du règlement (€)',
      cell: ({ row: { original: payment } }) => (
        <Badge text={payment.amount ?? '-'} variant="primary" />
      )
    },
    {
      accessorKey: 'date',
      header: 'Date limite',
      cell: ({ row: { original: payment } }) => (
        <Badge text={payment.date ? DateFormatter.DDMMYYYY(payment.date) : '-'} variant="grey" />
      )
    }
  ]

  const totalAmount = transfer?.totalPrice ?? 0
  const paidAmount = transferPayments?.data?.reduce((acc, curr) => acc + curr.amount, 0) ?? 0
  const remainingAmount = totalAmount - paidAmount

  return (
    <Fragment>
      <NestedDataTable<TransferPayment, null>
        ref={tableRef}
        actionTableLabel={
          isLoading || isLoadingPayments ? (
            <Loader />
          ) : (
            <div className="flex items-center gap-2">
              <Badge
                text={`Montant total ${NumberFormatter.formatCurrency(totalAmount)}`}
                variant="warning"
                className="rounded"
              />
              <Badge
                text={`Payé ${NumberFormatter.formatCurrency(paidAmount)}`}
                variant="fushia"
                className="rounded"
              />
              <Badge
                text={`Solde restant ${NumberFormatter.formatCurrency(remainingAmount)}`}
                variant="purple"
                className="rounded"
              />
            </div>
          )
        }
        title={'Paiements'}
        variant="action"
        deleteMessage={{
          name: 'paiement',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer ce paiement ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces paiements ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer tous les paiements ?'
        }}
        columns={columns}
        onDelete={invalidatePayments}
        onEditAction={(payment) => toggleModal('edit', payment)}
        endpoint={API.transfer.getAllPayments.url.replace(':transferId', transferId.toString())}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un paiement',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        isOpen={modalState.create}
        size="medium"
        icon={PriceTagIcon}
        title="Ajouter un règlement"
        onCancel={() => toggleModal('create')}
        hideActions
      >
        <TransferPaymentForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => {
              await createPayment({ ...values, transferId: transferId })
            }
          })}
        />
      </Modal>
      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier le règlement'}
          icon={BellIcon}
          hideActions
          onCancel={() => closeModel('edit')}
        >
          <TransferPaymentForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('edit'),
              onConfirm: async (values) =>
                updatePayment({ ...values, transferId: transferId, id: selectedData.id })
            })}
          />
        </Modal>
      )}
    </Fragment>
  )
}

export default TransferPayments
